import { AdminPageContent, AdminPageHeader } from "@/components/admin";
import { getLibraryDocuments, IFilter } from "@/api";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useDebouncedEffect, useMountEffect } from "@react-hookz/web";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { DataTable } from "./components/data-table";
import { DocumentsToolbar } from "./components/DocumentsToolbar";
import { columns } from "./components/columns";

function buildFilter(sorting: SortingState): IFilter {
    return {
        sort_by: sorting[0]?.id,
        order: sorting[0]?.desc ? "desc" : "asc",
    };
}

const AdminDocuments = () => {
    const { setBreadcrumbs } = useBreadcrumb();

    useDocumentTitle("Documents");

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [organizationId, setOrganizationId] = useState<number | null>(null);
    const [organizationName, setOrganizationName] = useState<string | null>(
        null
    );
    const [sorting, setSorting] = useState<SortingState>([
        { id: "created_at", desc: true },
    ]);
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });

    useMountEffect(() => {
        setBreadcrumbs([
            { label: "Admin", path: "/admin/home" },
            { label: "Documents", isActive: true },
        ]);
    });

    useDebouncedEffect(() => setDebouncedSearch(search), [search], 250, 1000);

    // Reset to first page when filters or sort changes
    useEffect(() => {
        setPagination((prev) =>
            prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
        );
    }, [debouncedSearch, organizationId, sorting]);

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: [
            "admin-documents",
            pagination,
            debouncedSearch,
            organizationId,
            sorting,
        ],
        queryFn: () =>
            getLibraryDocuments(
                pagination.pageIndex + 1,
                pagination.pageSize,
                buildFilter(sorting),
                undefined,
                organizationId,
                debouncedSearch || null
            ),
        placeholderData: keepPreviousData,
    });

    const hasActiveFilters = !!debouncedSearch || organizationId !== null;

    const clearAllFilters = () => {
        setSearch("");
        setDebouncedSearch("");
        setOrganizationId(null);
        setOrganizationName(null);
        setSorting([{ id: "created_at", desc: true }]);
    };

    return (
        <AdminPageContent>
            <AdminPageHeader
                title="Documents"
                subtitle="Manage uploaded insurance documents"
            />

            <div className="mt-6">
                <DocumentsToolbar
                    search={search}
                    onSearchChange={setSearch}
                    organizationId={organizationId}
                    organizationName={organizationName}
                    onOrgIdChange={setOrganizationId}
                    onOrgNameChange={setOrganizationName}
                />
                <DataTable
                    data={data?.items ?? []}
                    columns={columns}
                    rowCount={data?.total}
                    isLoading={isLoading}
                    isError={isError}
                    hasActiveFilters={hasActiveFilters}
                    onClearFilters={clearAllFilters}
                    onRetry={() => refetch()}
                    pagination={pagination}
                    onPaginationChange={setPagination}
                    sorting={sorting}
                    onSortingChange={setSorting}
                />
            </div>
        </AdminPageContent>
    );
};

export default AdminDocuments;
